import { create } from "zustand";
import type { CefrLevel, DialogSummary, CoverageResponse } from "@/types/dialog";
import { fetchDialogs, fetchCoverage } from "@/lib/content-api";

interface BrowseState {
  coverage: CoverageResponse | null;

  // Legacy single-language browse (homepage teaser)
  dialogs: DialogSummary[];
  language: string | null;
  level: CefrLevel | null;
  cursor: string | null;
  hasMore: boolean;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;

  // Multi-language feed
  feedDialogs: DialogSummary[];
  feedCursors: Record<string, string | null>;
  feedLoading: boolean;
  feedLoadingMore: boolean;
  feedHasMore: boolean;
  feedLoaded: boolean;
  feedLanguagesKey: string;

  loadCoverage: () => Promise<void>;
  loadDialogs: (language: string, level?: CefrLevel | null) => Promise<void>;
  loadMore: () => Promise<void>;
  setLanguage: (language: string | null) => void;
  setLevel: (level: CefrLevel | null) => void;

  loadFeed: (languages: string[], level?: CefrLevel | null) => Promise<void>;
  loadMoreFeed: () => Promise<void>;
}

const PER_LANGUAGE_BATCH = 6;
const DEFAULT_TOP_LANGUAGES = 200;

function isPublished(d: DialogSummary): boolean {
  return d.line_count > 0 && !!d.slug && !d.slug.startsWith("-");
}

function interleaveNewest(dialogs: DialogSummary[]): DialogSummary[] {
  if (dialogs.length <= 2) return dialogs;

  const sorted = [...dialogs].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );

  const result: DialogSummary[] = [];
  const remaining = [...sorted];

  while (remaining.length > 0) {
    const lastTwo = result.slice(-2).map((d) => d.language_code);
    const sameCount = lastTwo.length >= 2 && lastTwo[0] === lastTwo[1] ? 2 : 0;

    if (sameCount >= 2) {
      const blocked = lastTwo[0]!;
      const idx = remaining.findIndex((d) => d.language_code !== blocked);
      if (idx === -1) {
        result.push(...remaining);
        break;
      }
      result.push(remaining.splice(idx, 1)[0]!);
    } else {
      result.push(remaining.shift()!);
    }
  }

  return result;
}

function topLanguages(coverage: CoverageResponse, count: number): string[] {
  return Object.entries(coverage.by_language)
    .filter(([, v]) => v.published > 0)
    .sort(([, a], [, b]) => b.published - a.published)
    .slice(0, count)
    .map(([code]) => code);
}

export const useBrowseStore = create<BrowseState>((set, get) => ({
  coverage: null,
  dialogs: [],
  language: null,
  level: null,
  cursor: null,
  hasMore: false,
  loading: false,
  loadingMore: false,
  error: null,

  feedDialogs: [],
  feedCursors: {},
  feedLoading: false,
  feedLoadingMore: false,
  feedHasMore: false,
  feedLoaded: false,
  feedLanguagesKey: "",

  loadCoverage: async () => {
    try {
      const coverage = await fetchCoverage();
      set({ coverage });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to load coverage" });
    }
  },

  loadDialogs: async (language, level) => {
    set({ loading: true, error: null, dialogs: [], cursor: null, hasMore: false, language, level: level ?? null });
    try {
      const result = await fetchDialogs({
        language,
        level: level ?? undefined,
        limit: 30,
      });
      set({
        dialogs: result.dialogs,
        cursor: result.next_cursor,
        hasMore: !!result.next_cursor,
        loading: false,
      });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Failed to load dialogs", loading: false });
    }
  },

  loadMore: async () => {
    const { language, level, cursor, loadingMore } = get();
    if (!language || !cursor || loadingMore) return;

    set({ loadingMore: true });
    try {
      const result = await fetchDialogs({
        language,
        level: level ?? undefined,
        limit: 30,
        cursor,
      });
      set((state) => ({
        dialogs: [...state.dialogs, ...result.dialogs],
        cursor: result.next_cursor,
        hasMore: !!result.next_cursor,
        loadingMore: false,
      }));
    } catch {
      set({ loadingMore: false });
    }
  },

  setLanguage: (language) => {
    if (!language) {
      set({ language: null, dialogs: [], cursor: null, hasMore: false });
      return;
    }
    get().loadDialogs(language, get().level);
  },

  setLevel: (level) => {
    const { language } = get();
    set({ level });
    if (language) {
      get().loadDialogs(language, level);
    }
  },

  loadFeed: async (languages, level) => {
    const { coverage } = get();
    const hasPreferences = languages.length > 0;
    const langs = hasPreferences
      ? languages
      : coverage
        ? topLanguages(coverage, DEFAULT_TOP_LANGUAGES)
        : [];

    const key = [...langs].sort().join(",");
    if (get().feedLoaded && get().feedLanguagesKey === key) return;

    set({ feedLoading: true, feedDialogs: [], feedCursors: {}, feedHasMore: false, feedLoaded: false, feedLanguagesKey: key });

    if (langs.length === 0) {
      set({ feedLoading: false });
      return;
    }

    const batchSize = hasPreferences ? PER_LANGUAGE_BATCH : 2;

    try {
      const results = await Promise.allSettled(
        langs.map((lang) =>
          fetchDialogs({ language: lang, level: level ?? undefined, limit: batchSize }),
        ),
      );

      const allDialogs: DialogSummary[] = [];
      const cursors: Record<string, string | null> = {};

      results.forEach((result, i) => {
        const lang = langs[i]!;
        if (result.status === "fulfilled") {
          allDialogs.push(...result.value.dialogs.filter(isPublished));
          cursors[lang] = result.value.next_cursor;
        } else {
          cursors[lang] = null;
        }
      });

      const hasMore = Object.values(cursors).some((c) => c !== null);

      set({
        feedDialogs: interleaveNewest(allDialogs),
        feedCursors: cursors,
        feedHasMore: hasMore,
        feedLoading: false,
        feedLoaded: true,
      });
    } catch {
      set({ feedLoading: false });
    }
  },

  loadMoreFeed: async () => {
    const { feedCursors, feedLoadingMore, level } = get();
    if (feedLoadingMore) return;

    const langsWithMore = Object.entries(feedCursors).filter(([, c]) => c !== null);
    if (langsWithMore.length === 0) return;

    set({ feedLoadingMore: true });

    try {
      const results = await Promise.allSettled(
        langsWithMore.map(([lang, cursor]) =>
          fetchDialogs({
            language: lang,
            level: level ?? undefined,
            limit: PER_LANGUAGE_BATCH,
            cursor: cursor!,
          }),
        ),
      );

      const newDialogs: DialogSummary[] = [];
      const newCursors = { ...feedCursors };

      results.forEach((result, i) => {
        const lang = langsWithMore[i]![0];
        if (result.status === "fulfilled") {
          newDialogs.push(...result.value.dialogs.filter(isPublished));
          newCursors[lang] = result.value.next_cursor;
        } else {
          newCursors[lang] = null;
        }
      });

      const hasMore = Object.values(newCursors).some((c) => c !== null);

      set((state) => ({
        feedDialogs: [...state.feedDialogs, ...interleaveNewest(newDialogs)],
        feedCursors: newCursors,
        feedHasMore: hasMore,
        feedLoadingMore: false,
      }));
    } catch {
      set({ feedLoadingMore: false });
    }
  },
}));

export { topLanguages };
